import { Time } from '@/common/models/Time';
import TimeRange from '@/common/models/TimeRange';

import DaySchedule from './DaySchedule';

export const daypartLength = 7 * 24;
export const preciseDaypartLength = 7 * 24 * 60;

export enum TimeUnit {
  HOUR = 'hour',
  MINUTE = 'minute',
}

export interface IRange {
  from: number;
  to: number;
}

export interface IDaypartRange {
  dayRange: IRange;
  timeRange: TimeRange;
}

class WeekSchedule {
  public readonly daySchedules: Array<DaySchedule>;

  constructor(daySchedules: DaySchedule[] = Array(7).fill(new DaySchedule())) {
    this.daySchedules = daySchedules;
  }

  public clearWeek() {
    for (const daypartRange of this.daySchedules) {
      daypartRange.clearDay();
    }
  }

  public bookFullWeek() {
    for (const daypartRange of this.daySchedules) {
      daypartRange.bookFullDay();
    }
  }

  public scheduleRange(range: IDaypartRange) {
    for (let day = range.dayRange.from; day <= range.dayRange.to; day += 1) {
      this.daySchedules[day].scheduleRange(range.timeRange.copy());
    }
  }

  public freeRange(range: IDaypartRange) {
    for (let day = range.dayRange.from; day <= range.dayRange.to; day += 1) {
      this.daySchedules[day].freeRange(range.timeRange.copy());
    }
  }

  public isFreeAt(day: number, time: Time): boolean {
    return this.daySchedules[day]?.isTimeFree(time);
  }

  public toString(timeUnit: TimeUnit = TimeUnit.HOUR): string {
    return this.daySchedules.map((daypart) => daypart.toString(timeUnit)).join('');
  }

  public static parse(daypart: string): WeekSchedule {
    const daypartRanges = [];
    let dayLength = null;
    if (daypart.length === daypartLength) {
      dayLength = 24; // One day = 24 hours
    } else if (daypart.length === preciseDaypartLength) {
      dayLength = 24 * 60; // One day = 24 hours * 60 minutes
    } else {
      throw Error('Cannot parse daypart - Invalid length');
    }

    for (let day = 0; day < 7; day += 1) {
      const daypartSlice = daypart.slice(day * dayLength, (day + 1) * dayLength);
      const daypartRange = DaySchedule.parse(daypartSlice);
      daypartRanges.push(daypartRange);
    }
    return new WeekSchedule(daypartRanges);
  }
}

export default WeekSchedule;
